/**
 * Wordings of «Учитель» v3 — family «moments» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 *  - `v3.self.*` — «Сам»: the arrow is hidden, the child finds the move himself. A short challenge (1–2 sentences) that
 *    never names the answer; stages 2–5.
 *  - `v3.result.*` — the OUTCOME of a move the child made by following the arrow: what is true on the board now. Not
 *    praise and no «ты» (the move was Гамбитик's advice). A result pool has no sub-cases, so every wording is true for
 *    every sub-case of its idea (e.g. `develop` never promises «смотрит в центр»; `answerCheck` never says how).
 *    The child heard the idea tail (`v3.idea.<id>` / `v3.aim.<id>`) one move earlier: a result says the same fact in
 *    OTHER words, so it never sounds like an echo of the advice.
 *  - `v3.bark.quiet` — a 1–2 word nod to a quiet move (stage 1 / blitz), no bubble.
 */
import type { LessonWordings } from '../types.ts';

export const WORDINGS: LessonWordings = {
  // ───────────────────────── «Сам»: find it yourself (stages 2–5, 1–2 sentences, never the answer) ─────────────────────────

  'v3.self.develop': [
    { t: 'Какая наша фигура ещё спит дома? Разбуди её!' },
    { t: 'Дома ещё есть спящие фигуры. Кого позовём в игру?' },
    { t: 'Не все фигуры проснулись. Какую разбудим теперь?' },
    { t: 'Ещё одной фигуре пора в бой. Какой?' },
    { t: 'Дома кто-то скучает без дела. Выведи его {g:сам|сама}!' },
    { t: 'Посмотри, кто ещё не вышел из дома. Какой ход его выведет?' },
    { t: 'Развитие ещё не закончено. Какую фигуру выведем?', stages: [3, 5] },
    { t: 'Чья очередь выходить из дома? Найди!' },
  ],

  'v3.self.center': [
    { t: 'Пора заняться центром. Каким ходом?' },
    { t: 'Подумай, как нам побороться за центр.' },
    { t: 'Как сделать центр нашим? Найди {g:сам|сама}!' },
    { t: 'Центр — главная площадь доски. Что мы там сделаем?' },
    { t: 'Середина доски зовёт. Кто откликнется?' },
    { t: 'Вот он, центр. Каким ходом за него поборемся?' },
    { t: 'В центре есть работа для пешки. Для какой?' },
    { t: 'Центр — самое важное место. Каким ходом мы за него возьмёмся?' },
  ],

  'v3.self.attack': [
    { t: 'Кто-то у соперника стоит неудачно. Найдёшь, как напасть?' },
    { t: 'У соперника есть фигура, которую можно погонять. Чем?' },
    { t: 'Есть ход с нападением. Попробуй найти его {g:сам|сама}!' },
    { t: 'Какую фигуру соперника мы можем атаковать? Ищи!' },
    { t: 'Кто из наших может напасть на фигуру соперника? Поищи.' },
    { t: 'Время нападать! Каким ходом?' },
    { t: 'Найди ход, после которого фигуре соперника станет страшно.' },
    { t: 'Тут прячется нападение. Какая наша фигура его сделает?' },
  ],

  'v3.self.defend': [
    { t: 'Кому-то из наших нужна помощь. Помоги!' },
    { t: 'Одна наша фигура в беде. Как её выручить?' },
    { t: 'Нашей фигуре грозят. Что будем делать?' },
    { t: 'Сопернику приглянулась наша фигура. Как её уберечь?' },
    { t: 'Соперник подбирается к нашей фигуре. Выручишь её {g:сам|сама}?' },
    { t: 'Кого-то из наших могут съесть. Найдёшь спасение?' },
    { t: 'Нашим нужна подмога. Какой ход поможет?' },
    { t: 'Спасаем своих! Каким ходом?' },
  ],

  'v3.self.castle': [
    { t: 'Королю неуютно в центре. Как ему помочь?' },
    { t: 'Король хочет в безопасное место. Помоги ему!' },
    { t: 'Пора позаботиться о короле. Справишься {g:сам|сама}?' },
    { t: 'Как устроить королю домик? Найди!' },
    { t: 'Король ждёт своего домика. Какой ход к нему ведёт?' },
    { t: 'Подумай, как сделать королю безопаснее.' },
    { t: 'Домик для короля сам не построится. Какой ход нужен?' },
    { t: 'Где королю будет спокойнее? Найди ход, который ему поможет.' },
  ],

  'v3.self.improve': [
    { t: 'Какая наша фигура скучает? Найди ей место получше.' },
    { t: 'Кому-то из наших тесно. Куда его переставить?' },
    { t: 'Есть фигура, которая мало видит. Как сделать её сильнее?' },
    { t: 'Кому из наших пора на новое место? Поищи!' },
    { t: 'Все фигуры должны работать. Кто стоит без дела?' },
    { t: 'Переставь одну фигуру туда, где ей больше простора.' },
    { t: 'Подумай: какая наша фигура может стать активнее?', stages: [3, 5] },
    { t: 'Найди {g:сам|сама}, какую фигуру стоит переставить.' },
  ],

  'v3.self.plan': [
    { t: 'Вспомни наш план. Какой ход его продолжит?' },
    { t: 'Помнишь, что мы сегодня задумали? Найди следующий шаг.' },
    { t: 'Наш план ещё не закончен. Что дальше?' },
    { t: 'Что у нас по плану? Найди ход {g:сам|сама}.' },
    { t: 'План у нас есть. Какой ход ему поможет?' },
    { t: 'У нас сегодня есть задумка. Какой ход к ней ведёт?' },
    { t: 'Продолжи наш план — что сделаем теперь?' },
    { t: 'Наша цель на сегодня подскажет ход. Найдёшь его?' },
  ],

  'v3.self.endgame': [
    { t: 'Фигур на доске немного. Как нам теперь играть?' },
    { t: 'В конце партии король тоже воюет. Найдёшь сильный ход?' },
    { t: 'Теперь каждая фигура на счету, и король тоже. Какой ход выберешь?' },
    { t: 'Помни: когда фигур мало, помогает даже король. Найдёшь ход {g:сам|сама}?' },
    { t: 'Фигур мало — и доска стала просторной. Куда двинемся?' },
    { t: 'Подумай, чья помощь сейчас важнее всего. Найди ход!' },
    { t: 'Сейчас решает каждый шаг. Какой сделаем мы?' },
    { t: 'В эндшпиле важна каждая мелочь. Найдёшь нужный ход?', stages: [5, 5] },
  ],

  'v3.self.generic': [
    { t: 'Подумай: что хочет соперник и что можем мы?' },
    { t: 'Сначала посмотри, что задумал соперник. А потом найди наш ход.' },
    { t: 'Я спрячу стрелку. Найдёшь ход {g:сам|сама}?' },
    { t: 'Посмотри на всю доску. Какой ход тут просится?' },
    { t: 'Не спеши: сначала угрозы соперника, потом наш план.' },
    { t: 'Давай без подсказки: какой ход ты выберешь?' },
    { t: 'Я уже вижу хороший ход. А ты?' },
    { t: 'Тут есть ход получше остальных. Поищешь?' },
  ],

  // ───────────────────────── the outcome of a followed arrow (not praise, no «ты») ─────────────────────────

  'v3.result.develop': [
    { t: '{Конь} в игре — одной спящей фигурой меньше.' },
    { t: 'Готово: {конь} уже не дома, а в бою.' },
    { t: 'Дома стало просторнее: {конь} {p:ушёл|ушла} на задание.' },
    { t: 'Смотри: теперь {конь} стреляет по новым клеткам.' },
    { t: 'Будильник сработал: {конь} в деле.' },
    { t: '{Конь} уже на ногах и {p:готов|готова} к делу.' },
    { t: 'Ну вот, {конь} больше не скучает дома.' },
    { t: 'Лёгкая фигура вышла из дома — это и есть развитие.', stages: [3, 5] },
  ],

  'v3.result.centerPawn': [
    { t: 'Пешка в центре — теперь она смотрит на клетки соперника.' },
    { t: 'Место в центре занято — нашей пешкой.' },
    { t: 'Флажок в центре поставлен — это наша пешка.' },
    { t: 'Одна из главных клеток доски теперь наша.' },
    { t: 'Середина доски под присмотром нашей пешки.' },
    { t: 'Центр встречает нашу пешку.' },
    { t: 'Пешка шагнула прямо в середину.' },
    { t: 'В самом сердце доски — наша пешка.' },
  ],

  'v3.result.fightCenter': [
    { t: 'Наша пешка напала на пешку соперника в центре.' },
    { t: 'Теперь пешка соперника в центре под ударом.' },
    { t: 'Теперь в центре идёт спор: чья пешка сильнее?' },
    { t: 'Соперник больше не спокоен за свой центр.' },
    { t: 'Соперник теперь думает, как быть с пешкой в центре.' },
    { t: 'В центре закипело: наша пешка нападает.' },
    { t: 'Наша пешка толкается с чужой за центр.' },
  ],

  'v3.result.supportCenter': [
    { t: 'Пешка поспешила на помощь центру.' },
    { t: 'Теперь у центра есть подмога.' },
    { t: 'Маленький шаг пешкой — а центру уже легче.' },
    { t: 'Наша пешка прикрывает центр сзади.' },
    { t: 'Ещё одна пешка работает на центр.' },
    { t: 'Опора готова: пешка держит центр.' },
    { t: 'Пешки помогают друг другу — так и строится центр.' },
  ],

  'v3.result.openLine': [
    { t: 'Пешка ушла с дороги — теперь нашей фигуре есть где развернуться.' },
    { t: 'Фигура, которая стояла за пешкой, теперь видит дальше.' },
    { t: 'Пешка шагнула — и выпустила фигуру на волю.' },
    { t: 'Вот эта дорожка теперь свободна.' },
    { t: 'Стало просторнее: у нашей фигуры открылась новая дорога.' },
    { t: 'Дверь открыта: фигура сзади может выходить.' },
    { t: 'Раз — и дорога для фигуры открыта!' },
    { t: 'Дальнобойная фигура получила свободную линию.', stages: [3, 5] },
  ],

  'v3.result.aimWeakSquare': [
    { t: 'Оттуда {конь} смотрит прямо на соседа чужого короля.' },
    { t: 'Наша фигура нашла дорожку к слабому месту соперника.' },
    { t: 'Теперь у короля соперника появилась забота.' },
    { t: 'Слабое место у короля соперника под присмотром {коня:gen}.' },
    { t: 'Есть прицел на слабое место — соперник должен быть начеку.' },
    { t: 'Так начинается охота на слабое место соперника.' },
    { t: 'Сосед короля соперника теперь под ударом.' },
    { t: 'Слабая клетка у короля соперника теперь под давлением.', stages: [3, 5] },
  ],

  'v3.result.prepareCastle': [
    { t: 'Коридор к домику короля свободен.' },
    { t: 'Последняя помеха ушла с дороги короля.' },
    { t: 'Король и ладья теперь смотрят друг на друга.' },
    { t: 'До рокировки рукой подать.' },
    { t: 'Путь в домик открыт — дело за королём.' },
    { t: 'Скоро у короля новоселье.' },
    { t: 'Фигуры больше не мешают рокировке.' },
  ],

  'v3.result.castle': [
    { t: 'Король спрятался, а ладья вышла поработать.' },
    { t: 'Укрытие найдено: король ушёл из центра.' },
    { t: 'Вот и король в домике.' },
    { t: 'Ладья пришла из угла поближе к центру.' },
    { t: 'Готово: король переехал в свой домик.' },
    { t: 'В домике до короля труднее добраться.' },
    { t: 'Хоп — и ладья уже рядом с королём.' },
  ],

  'v3.result.connectRooks': [
    { t: 'Теперь каждая ладья прикрывает соседку.' },
    { t: 'Все, кто мешал, ушли — ладьи на связи.' },
    { t: 'Две ладьи — одна команда!' },
    { t: 'Ладьи дотянулись друг до друга.' },
    { t: 'Ладьи встретились на своём ряду.' },
    { t: 'Подружились ладьи: между ними пусто.' },
    { t: 'Одна ладья теперь поможет другой.' },
    { t: 'Соединённые ладьи — знак, что дебют кончается.', stages: [3, 5] },
  ],

  'v3.result.attack': [
    { t: 'Теперь {конь} соперника под ударом.' },
    { t: 'Мы напали на {коня} — пусть соперник думает, как {его} спасти.' },
    { t: '{Конь} соперника на прицеле — {ему} стало неуютно.' },
    { t: 'Есть нападение: {конь} соперника в опасности.' },
    { t: 'Соперник должен спасать {коня}, иначе мы {его} заберём.' },
    { t: 'Охота началась: цель — {конь} соперника.' },
    { t: 'Наша фигура грозит съесть {коня}.' },
    { t: 'Пора сопернику уводить {коня}.' },
  ],

  'v3.result.freeCapture': [
    { t: '{Конь} соперника теперь {p:наш|наша} — бесплатно!' },
    { t: 'Забрали {коня} и ничего не отдали взамен.' },
    { t: 'Подарок принят: {конь} соперника у нас.' },
    { t: 'Съели {коня} даром — назад не отобрать.' },
    { t: 'У соперника стало на {коня} меньше.' },
    { t: 'Хрум — и {конь} соперника {p:съеден|съедена} даром.' },
    { t: 'Мы в плюсе: съели {коня} без потерь.' },
    { t: '{Конь} соперника {p:отправился|отправилась} к нам в коробку.' },
  ],

  'v3.result.winMaterial': [
    { t: 'Взяли {коня} — пусть соперник отвечает, выгода за нами.' },
    { t: '{Конь} соперника {p:съеден|съедена}, и счёт теперь в нашу пользу.' },
    { t: 'Выгодное взятие: получили больше, чем можем отдать.' },
    { t: 'Посчитали — и забрали {коня} с выгодой.' },
    { t: 'В нашей копилке прибавилось: {конь} соперника у нас.' },
    { t: 'Сторож не уберёг — {конь} соперника у нас.' },
    { t: 'Размен ещё не кончился, а мы уже в плюсе.' },
  ],

  'v3.result.recapture': [
    { t: 'Взяли у нас — взяли и мы.' },
    { t: 'Наша фигура пропала не даром — мы забрали {коня}.' },
    { t: 'Ответное взятие: {конь} соперника тоже {p:съеден|съедена}.' },
    { t: 'Соперник съел нашу фигуру, а мы — {коня}.' },
    { t: 'Мы не остались в долгу: {конь} соперника теперь {p:наш|наша}.' },
    { t: 'Дали сдачи — {конь} соперника у нас.' },
    { t: 'Теперь и соперник кое-что потерял — {коня}.' },
  ],

  'v3.result.trade': [
    { t: 'Размен начался: {коня} мы уже забрали.' },
    { t: 'Равный размен: отдаём столько же, сколько берём.' },
    { t: '{Конь} соперника за нашу фигуру — всё по-честному.' },
    { t: 'Меняемся так, что никому не обидно.' },
    { t: '{Коня} забрали, но и соперник может взять в ответ.' },
    { t: 'Фигур на доске стало меньше — так бывает при размене.' },
    { t: 'Как в магазине: берём и платим поровну.' },
  ],

  'v3.result.escape': [
    { t: '{Конь} {p:отбежал|отбежала} в безопасное место.' },
    { t: 'Спасли {коня}: соперник остался без добычи.' },
    { t: 'Уф, {конь} в безопасности.' },
    { t: 'Фигура цела, беда прошла стороной.' },
    { t: 'Опасность позади: {конь} {p:спрятался|спряталась}.' },
    { t: 'Выручили {коня} — {он} теперь на спокойной клетке.' },
    { t: 'Прятки удались: {конь} теперь вне опасности.' },
  ],

  'v3.result.defend': [
    { t: 'У {коня:gen} теперь есть охранник.' },
    { t: '{Конь} под защитой — даром {его} не взять.' },
    { t: 'Помощь пришла: {конь} в безопасности.' },
    { t: 'Караул на месте: {конь} под присмотром.' },
    { t: 'Наши своих не бросают: {конь} {p:защищён|защищена}.' },
    { t: 'Съест соперник {коня} — мы съедим обидчика.' },
    { t: 'Теперь за {коня} соперник заплатит своей фигурой.' },
  ],

  'v3.result.defendMate': [
    { t: 'Дверь для мата закрыта.' },
    { t: 'Защитились: мата в один ход больше нет.' },
    { t: 'Фух, до мата соперник не дотянулся.' },
    { t: 'Опасный удар соперника больше не работает.' },
    { t: 'Хитрый план соперника с матом сорвался.' },
    { t: 'Короля прикрыли вовремя.' },
    { t: 'Ловушка для короля не сработала.' },
    { t: 'Мат был совсем рядом, а теперь его нет.' },
  ],

  'v3.result.answerCheck': [
    { t: 'Спаслись от шаха — можно думать дальше.' },
    { t: 'Отбились от шаха, и король в порядке.' },
    { t: 'Этот шах мы пережили.' },
    { t: 'Королю больше не шах — можно выдохнуть.' },
    { t: 'С шахом разобрались.' },
    { t: 'Выкрутились из шаха!' },
    { t: 'Всё, шаха нет.' },
  ],

  'v3.result.check': [
    { t: 'Король соперника под шахом.' },
    { t: 'Соперник должен отвечать на шах.' },
    { t: 'Соперник теперь думает только о короле.' },
    { t: 'Королю соперника не до нападений — у него шах.' },
    { t: 'Шах поставлен — посмотрим, как спасётся король.' },
    { t: 'Тук-тук, королю соперника шах.' },
    { t: 'Первым делом сопернику придётся спасать короля.' },
  ],

  'v3.result.improvePiece': [
    { t: 'Отсюда {конь} видит больше, чем раньше.' },
    { t: 'У {коня:gen} стало больше ходов.' },
    { t: 'Теперь {конь} при деле.' },
    { t: 'Чем больше дорог, тем сильнее фигура.' },
    { t: '{Конь} {p:перебрался|перебралась} туда, где {ему} просторнее.' },
    { t: 'На новом месте у {коня:gen} больше работы.' },
    { t: 'Смотри, сколько клеток теперь под присмотром {коня:gen}.' },
  ],

  'v3.result.passedPawn': [
    { t: '{Конь} {p:подобрался|подобралась} ближе к последнему ряду.' },
    { t: 'Дорога до конца доски стала короче.' },
    { t: 'Шажок сделан — финиш стал ближе.' },
    { t: 'Вот эта дорожка ведёт к превращению — и мы по ней идём.' },
    { t: '{Конь} упрямо шагает вперёд.' },
    { t: 'На шаг ближе к превращению!' },
    { t: 'В конце пути {конь} может стать новой сильной фигурой.' },
    { t: 'Проходная бежит к цели.', stages: [5, 5] },
  ],

  'v3.result.kingActivity': [
    { t: 'Король вышел в бой.' },
    { t: 'Наш король спешит на помощь.' },
    { t: 'Теперь король не прячется, а воюет.' },
    { t: 'Смелый шаг: король идёт в дело.' },
    { t: 'Раз-два, король на марше!' },
    { t: 'Самая важная фигура тоже взялась за работу.' },
    { t: 'Король идёт туда, где он нужнее.' },
    { t: 'Активный король в эндшпиле — большая сила.', stages: [5, 5] },
  ],

  'v3.result.rookOpenFile': [
    { t: 'Теперь наши пешки ладье не мешают.' },
    { t: 'У ладьи появилась своя дорога.' },
    { t: 'Вот на этой линии ладье просторно.' },
    { t: 'Ладья выбралась из-за своих пешек.' },
    { t: 'Ладья вздохнула свободно.' },
    { t: 'Ладье больше не тесно за своими пешками.' },
    { t: 'Выход найден: у ладьи своя линия.' },
    { t: 'Вертикаль без наших пешек теперь в руках ладьи.', stages: [3, 5] },
  ],

  // ───────────────────────── the quiet nod (1–2 words, no bubble) ─────────────────────────

  'v3.bark.quiet': [
    { t: 'Ага!' },
    { t: 'Так-так.' },
    { t: 'Угу.' },
    { t: 'Понятно.' },
    { t: 'Ясно.' },
    { t: 'Вижу.' },
    { t: 'Понял.' },
    { t: 'Идём дальше.' },
    { t: 'Ага, ясненько.' },
    { t: 'Поехали дальше.' },
    { t: 'Принято.' },
    { t: 'Есть.' },
  ],
};
